import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { catchError, concatMap, from, map, Observable, throwError, toArray } from 'rxjs';
import { WarehouseError } from '@app/store/shared/models';
import { Pallet, PalletDetails, PalletDetailsDto, PalletDto, PalletStatus } from '../models';
import { mapDto } from '@shared/operators';
import { ApiResponse, hasMore, PaginatedApiResponse } from '@shared/models/api-response';
import { uuid } from '@shared/utils/uuid';

const PAGE_SIZE = 20;

@Service()
export class HttpService {
    private readonly httpClient = inject(HttpClient);

    loadPallets(status: PalletStatus, page: number): Observable<{ pallets: Pallet[]; hasMore: boolean }> {
        return this.httpClient
            .get<PaginatedApiResponse<PalletDto[]>>('pallets', { params: { status, sort: 'createdAt', order: 'desc', page, limit: PAGE_SIZE } })
            .pipe(
                mapDto((response) => ({
                    pallets: response.data.map((dto) => new Pallet(dto)),
                    hasMore: hasMore(response),
                })),
            );
    }

    generatePallets(quantity: number): Observable<void> {
        return from(Array.from({ length: quantity })).pipe(
            concatMap(() => this.httpClient.post<ApiResponse<PalletDetailsDto>>('pallets', {})),
            toArray(),
            map(() => undefined),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('pallets.new.error')))),
        );
    }

    printPallet(code: string): Observable<void> {
        return this.httpClient
            .skipErrorMessageInterceptor()
            .post(`pallets/${code}/print`, { jobKey: uuid() })
            .pipe(
                map(() => undefined),
                catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('pallets.list.print-error')))),
            );
    }

    loadPallet(code: string): Observable<PalletDetails> {
        return this.httpClient
            .skipErrorMessageInterceptor()
            .get<ApiResponse<PalletDetailsDto>>(`pallets/${code}`)
            .pipe(mapDto((response) => new PalletDetails(response.data)));
    }

    addCarton(code: string, cartonCode: string): Observable<PalletDetails> {
        return this.httpClient.post<ApiResponse<PalletDetailsDto>>(`pallets/${code}/cartons`, { cartonCode }).pipe(
            mapDto((response) => new PalletDetails(response.data)),
            catchError((response: HttpErrorResponse) =>
                throwError(() => new WarehouseError(response, response.status === 404 ? marker('pallets.add.unknown') : marker('pallets.add.error'))),
            ),
        );
    }

    removeCarton(code: string, cartonCode: string): Observable<PalletDetails> {
        return this.httpClient.delete<ApiResponse<PalletDetailsDto>>(`pallets/${code}/cartons/${cartonCode}`).pipe(
            mapDto((response) => new PalletDetails(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('pallets.remove.error')))),
        );
    }

    closePallet(code: string): Observable<PalletDetails> {
        return this.httpClient.post<ApiResponse<PalletDetailsDto>>(`pallets/${code}/close`, {}).pipe(
            mapDto((response) => new PalletDetails(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('pallets.close.error')))),
        );
    }
}
